import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
    List,
    Edit,
    Create,
    Datagrid,
    SimpleForm,
    TextInput,
    SelectInput,
    DeleteButton,
    EditButton,
    EmailField,
    TextField,
    BooleanInput,
    NumberInput,
    ReferenceInput,
    ReferenceArrayInput,
    SelectArrayInput,
    Button,
    FunctionField,
    Loading,
    Error,
    useRefresh,
    Labeled,
} from "react-admin";

import LargePagination from "../core/LargePagination";
import { config } from "../../config";
import { userService } from "../../services/UserService";
import { LVEditToolbar, LVCreateToolbar } from "../core/toolbars";

import Filter from "../core/Filter";
import buildNewUrl from "../../helpers/buildNewUrl";

const BreakField = () => <br />;
const ItalicField = ({ children }) => <i>{children}</i>;
const SectionTitle = ({ children }) => <div>{children}</div>;
const SpanField = ({ children }) => <span>{children}</span>;

const classes = {
    textInput: {
        display: "flex",
        width: "20rem",
    },
};

export const UserList = ({ ...props }) => {
    const location = useLocation();
    const navigate = useNavigate();

    return (
        <>
            <Filter
                filterKeys={["username", "firstname", "lastname"]}
                labels={{ username: "Username", firstname: "First Name", lastname: "Last Name" }}
                onChange={(newFilterParams) => {
                    navigate(buildNewUrl(location, newFilterParams, true));
                }}
            />

            <List title="All users" perPage={500} pagination={<LargePagination />} {...props}>
                <Datagrid bulkActionButtons={false}>
                    <TextField source="id" />
                    <TextField source="username" />
                    <TextField source="lastname" label="Last Name" />
                    <TextField source="firstname" label="First Name" />
                    <TextField source="status" />
                    <TextField source="passwordExpirationPolicyDays" label="Password Expiration" />
                    <EmailField source="email" />
                    <EditButton />
                    <DeleteButton mutationMode="pessimistic" />
                </Datagrid>
            </List>
        </>
    );
};

const UserTitle = ({ record }) => {
    return <span>User {record ? `"${record.username}"` : ""}</span>;
};

// http://ziptasticapi.com/47122  NOTE: we can use this in the future to get a city/state

const validateUserCreation = (values) => {
    const errors = {};

    if (!values.firstname) {
        errors.firstname = "The firstname is required";
    }

    if (!values.email) {
        errors.email = "The email field is required";
    }

    if (!values.username) {
        errors.username = "The Username field is required";
    } else if (values.username.length < 4) {
        errors.username = "Username must be 4 or more characters";
    }

    if (!values.rolegroupids) {
        errors.rolegroupids = "The Role Group List field is required";
    }

    return errors;
};

const CoreFields = () => {
    return (
        <>
            <TextInput label="Username" source="username" sx={classes.textInput} />
            <TextInput source="email" sx={classes.textInput} />
            <TextInput source="firstname" label="First Name" sx={classes.textInput} />
            <TextInput source="lastname" label="Last Name" sx={classes.textInput} />
            <SelectInput
                label="Active Status"
                source="status"
                choices={[
                    { id: "active", name: "Active" },
                    { id: "pending", name: "Pending" },
                    { id: "deactivated", name: "Deactivated" },
                    { id: "locked", name: "Locked" },
                    { id: "migrating_pending", name: "Migration Pending" },
                    { id: "migrated", name: "Migrated" },
                ]}
            />
            <BooleanInput label={`Demo User`} source="isDemoUser" />
            <BooleanInput label="Is Allowed Concurrent Sessions" source="allowConcurrentSessions" />

            <Labeled label="Password expires after this many day. Leave blank for `No Expiration`.">
                <NumberInput source="passwordExpirationPolicyDays" sx={classes.textInput} />
            </Labeled>
        </>
    );
};

export const UserCreate = (props) => (
    <Create title="Create User" {...props}>
        <SimpleForm
            validate={validateUserCreation}
            initialValues={{ doSendEmailToCustomer: true }}
            toolbar={<LVCreateToolbar />}
        >
            <SectionTitle>Create new user</SectionTitle>

            <CoreFields classes={classes} />

            <ReferenceInput
                perPage={0}
                sort={{ field: "clientName", order: "ASC" }}
                label="Client"
                source="clientId"
                reference="portal/clients"
            >
                <SelectInput optionText={(client) => client.clientId + ": " + client.clientName} />
            </ReferenceInput>

            <ReferenceArrayInput
                label="Site Group List"
                source="siteGroupListId"
                reference="portal/siteGroups"
                style={{ width: "100%" }}
            >
                <SelectArrayInput optionText={(s) => s.siteGroupListId + ": " + s.site_group_name} />
            </ReferenceArrayInput>

            <ReferenceArrayInput
                label="Role Group List"
                source="rolegroupids"
                reference="portal/roleGroups"
                style={{ width: "100%" }}
            >
                <SelectArrayInput optionText={(rg) => (rg ? rg.id + ": " + rg.name : "")} />
            </ReferenceArrayInput>

            <BooleanInput label="Email Customer" source="doSendEmailToCustomer" />
            <ItalicField>(Generate an email to the customer with the temporary password.)</ItalicField>
        </SimpleForm>
    </Create>
);

export const UserEdit = (props) => {
    const refresh = useRefresh();
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [resetPassword, setResetPassword] = useState(false);

    const resetMfaSettings = async (userId) => {
        setLoading(true);
        try {
            const response = await fetch(
                new Request(`${config.apiUrl}/users/resetMfa/${userId}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `${userService.authToken}`,
                    },
                })
            );
            const body = await response.json();

            if (!body.success) {
                setError(body.error);
            } else {
                refresh();
            }
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <Loading />;
    } else if (error) {
        return <Error error={error} />;
    } else {
        return (
            <Edit title={<UserTitle />} {...props}>
                <SimpleForm validate={validateUserCreation} toolbar={<LVEditToolbar />}>
                    <TextInput disabled source="id" />

                    <CoreFields classes={classes} />

                    <ReferenceInput perPage={0} label="Client" source="clientId" reference="portal/clients">
                        <SelectInput optionText="clientName" />
                    </ReferenceInput>

                    <ReferenceArrayInput
                        label="Site Group List"
                        source="sitegroups"
                        reference="portal/siteGroups"
                        style={{ width: "100%" }}
                    >
                        <SelectArrayInput optionText={(s) => (s ? s.siteGroupListId + ": " + s.site_group_name : "")} />
                    </ReferenceArrayInput>

                    <ReferenceArrayInput
                        label="Role Group List"
                        source="rolegroupids"
                        reference="portal/roleGroups"
                        style={{ width: "100%" }}
                    >
                        <SelectArrayInput optionText={(rg) => (rg ? rg.id + ": " + rg.name : "")} />
                    </ReferenceArrayInput>

                    <FunctionField
                        label="Multi-factor Authentication"
                        render={(record) => (
                            <>
                                <SpanField>Chosen Type: {record.mfaType || "N/A"}</SpanField>
                                <BreakField />
                                <Button
                                    disabled={!record.mfaType}
                                    variant="contained"
                                    color="primary"
                                    onClick={() => resetMfaSettings(record.id)}
                                    label="Reset MFA Settings"
                                />
                            </>
                        )}
                    />

                    <BreakField />
                    <BooleanInput
                        label="Reset Users Password"
                        onChange={(event) => {
                            setResetPassword(!resetPassword);
                            console.log(this, event);
                        }}
                        source="doResetUserPassword"
                    />
                    <ItalicField>
                        Caution!: This generates a new temporary password and uses the below email options..
                    </ItalicField>
                    <BreakField />
                    <BooleanInput label="Email Customer" source="doSendEmailToCustomer" disabled={!resetPassword} />
                    <ItalicField>
                        (Generate an email to the customer with the temporary password. Otherwise - you will receive
                        it.)
                    </ItalicField>
                    <BreakField />
                </SimpleForm>
            </Edit>
        );
    }
};
